import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { categories, games } from "@/mocks/games";
import { filterGames, getPopularGames, paginate, sortGames, type SortKey } from "@/utils/gameUtils";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import GameCard from "@/components/feature/GameCard";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "hot", label: "热度优先" },
  { key: "new", label: "最新上架" },
  { key: "rating", label: "评分优先" },
];

const PAGE_SIZE = 8;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  const [input, setInput] = useState(query);
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 网页游戏直接玩` : "全站搜索 - 网页游戏直接玩",
    description: query
      ? `在网页游戏直接玩中搜索“${query}”相关的网页游戏，支持关键词查询、类型筛选与排序，免下载在线试玩。`
      : "搜索网页游戏直接玩平台上的网页游戏，支持关键词、类型筛选与排序。",
    keywords: "网页游戏搜索,在线试玩,网页游戏直接玩",
  });

  const results = useMemo(() => {
    const base = query.trim() ? filterGames(games, { keyword: query, category }) : category ? filterGames(games, { category }) : [];
    return sortGames(base, sort);
  }, [query, category, sort]);

  const paged = useMemo(() => paginate(results, page, PAGE_SIZE), [results, page]);
  const popular = useMemo(() => getPopularGames(4), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-screen2xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "全站搜索" }]} />

      <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <h1 className="text-2xl font-black text-foreground-900 md:text-3xl">全站搜索</h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入游戏名称、类型或标签关键词，快速找到想玩的网页游戏。
        </p>

        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row" role="search">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="text"
              name="keyword"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="例如：竞技、角色扮演、赛车、塔防"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
          >
            <i className="ri-search-line"></i>
            搜索游戏
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-foreground-400">按类型：</span>
          <button
            type="button"
            onClick={() => {
              setCategory("");
              setPage(1);
            }}
            className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
              category === ""
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            全部类型
          </button>
          {categories.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                setCategory(item.key === category ? "" : item.key);
                setPage(1);
              }}
              className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
                category === item.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          {query.trim() ? (
            <>
              搜索 <strong className="text-foreground-900">“{query}”</strong> ，共找到{" "}
              <strong className="text-primary-600">{results.length}</strong> 款游戏
            </>
          ) : (
            <>
              请输入关键词开始搜索
              {category && (
                <>
                  ，当前筛选 <strong className="text-foreground-900">{results.length}</strong> 款游戏
                </>
              )}
            </>
          )}
        </p>
        <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                setSort(option.key);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition ${
                sort === option.key ? "border border-background-200 bg-background-50 text-primary-600" : "border border-transparent text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {paged.items.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {paged.items.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
            <Pagination page={paged.page} totalPages={paged.totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="space-y-8">
            <EmptyState
              icon="ri-search-eye-line"
              title={query.trim() ? "未找到相关游戏" : "暂无搜索结果"}
              description={
                query.trim()
                  ? `没有找到与“${query}”匹配的游戏，试试更换关键词，或看看下面的热门推荐。`
                  : "输入关键词后即可搜索游戏，也可以直接浏览下方的热门推荐。"
              }
            />
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-foreground-900">
                <i className="ri-fire-line text-primary-500"></i>
                热门游戏推荐
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {popular.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}